import React, { useContext, useState } from 'react';
// Import from specific module paths rather than the '../components' mega-barrel. The barrel
// re-exports './forms' and './clientsale', whose Firebase init modules (client_sale.js etc.) run
// initializeApp() at module scope — a side effect webpack cannot tree-shake. Pulling the barrel
// here therefore ships the entire Firebase SDK in the every-page app bundle. Direct paths avoid it.
import { Header } from '../components/header';
import { Footer } from '../components/footer';
import { SignUpModal } from '../components/shared/SignUpModal';
import { ConsultationForm, SelfScheduleConsultStub } from '../components/forms/consult';
import { OnPageSelfScheduleForm } from '../components/forms/selfSchedule';
import { Chat } from '../components/chat/chat';
import { GlobalSiteDataContext } from '../context';
import { hardTrim, minLayout } from '../utils';
import '../utils/styles/global.css';
// Page-scoped stylesheets with no always-rendered code owner to co-locate with. The area pages
// (/areas/bikini, /areas/legs, /areas/underarm, /areas/brazilian, /inclusive-clientele/for-men)
// are Builder-authored — the id (#bikini-page, …) is set in Builder content and their legacy hero
// components are unused — and socialHero.css's component/template (SocialHero/LpSocialTemplate) is
// likewise unused (the live /lp/social page renders LandingPage with #social-page). Gatsby merges
// all CSS into one global stylesheet and every rule here is page-id-scoped, so importing from
// Layout keeps them in the build without affecting other pages.
//
// All page CSS that HAS a rendered owner is co-located with it (the CSS analog of the
// RegisteredBuilderComponent wrapper — coupling via the import graph, not a manual list here):
//   googlelp.css → GoogleLPHero, allbodyareas.css → AreasHero, darkskin.css → DarkSkinHero,
//   lip-and-chin.css → LipChinHero, promolp.css → PromoModule, AnniversarySaleHero.css → AnniversarySaleHero.
import '../components/areas/men/men.css';
import '../components/areas/bikini/bikini.css';
import '../components/areas/brazilian/brazilian.css';
import '../components/areas/legs/legs.css';
import '../components/areas/underarm/underarm.css';
import '../components/lp/social/socialHero.css';
import { config } from '@fortawesome/fontawesome-svg-core';
import '@fortawesome/fontawesome-svg-core/styles.css';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { Script } from 'gatsby';
import { useCountdownActive } from '../hooks/useCountdownActive';
import { useSelfScheduleFlags } from '../hooks/useSelfScheduleVariant';
import { useSelfScheduleCtaRedirect } from '../hooks/useSelfScheduleCtaRedirect';

config.autoAddCss = false;

const ABTestPathnames = ['/lp/google-v2', '/homepage-v2', '/'];

export function Layout({ children, location }) {
    const siteData = useContext(GlobalSiteDataContext);
    const [showSignUpModal, setShowSignUpModal] = useState(false);
    const showCountdown = useCountdownActive();

    // WEBDEV-3681 (variant '1') / WEBDEV-4173 (variant '2'): when the self-schedule
    // experiment is active (see SELF_SCHEDULE_PATHS), redirect all consult-form CTAs
    // to the external /schedule app and hide the legacy on-page ConsultationForm.
    // Variant '2' additionally shows the on-page self-schedule handoff form
    // (WEBDEV-4039) in its place — it keeps the same hidden-cloudamp-input behavior
    // the stub it replaces had, so downstream forms still get attribution data.
    const { active: selfScheduleActive, onPageFormActive: selfScheduleOnPageFormActive } = useSelfScheduleFlags();

    // The interceptor is document-level, so mounting it here in the (persistent)
    // Layout covers CTAs on every self-schedule page, Builder-placed or React.
    useSelfScheduleCtaRedirect(selfScheduleActive);

    // ABTest / influencer pages render their own ConsultationForm; everyone else
    // uses Layout's global one. Only swap the form here where Layout owns it —
    // so self-schedule pages that render their own form (handled on the page)
    // don't get a second, conflicting cloudamp input here.
    const usesGlobalConsultForm =
        !ABTestPathnames.includes(location.pathname) && !location.pathname.startsWith('/lp/influencer/');
    const showConsultForm = usesGlobalConsultForm && !selfScheduleActive;
    const showOnPageForm = usesGlobalConsultForm && selfScheduleOnPageFormActive;
    const showSelfScheduleStub = selfScheduleActive && !showOnPageForm;

    return (
        <div
            id={`${location.pathname === '/' ? 'home' : hardTrim(location.pathname)}-page-wrapper`}
            className={`${minLayout[hardTrim(location.pathname)] ? 'min-layout' : ''} ${showCountdown ? 'with-countdown' : ''}`}
        >
            {location.pathname !== '/lp/google-v2' && <Header siteData={siteData} showCountdown={showCountdown} />}
            <GoogleReCaptchaProvider reCaptchaKey={process.env.GATSBY_RECAPTCHA_SITE_KEY} scriptProps={{ async: true }}>
                {children}
                {showConsultForm && <ConsultationForm siteData={siteData} location={location} />}
                {showOnPageForm && <OnPageSelfScheduleForm promo={{ placement: 'site-wide' }} />}
                {showSelfScheduleStub && <SelfScheduleConsultStub />}
                {showSignUpModal && <SignUpModal {...{ siteData, showSignUpModal, setShowSignUpModal }} />}
                <Footer siteData={siteData} setShowSignUpModal={setShowSignUpModal} />
                <Chat />
            </GoogleReCaptchaProvider>
            <Script id="cloudamp">
                {`
        window._cloudAmp = window._cloudAmp || {};
        _cloudAmp.forms = [];
        (function () {
          var scripts = document.getElementsByTagName('script'),
              sLen = scripts.length,
              ca_script = document.createElement('script'),
              head = document.getElementsByTagName('head'),
              protocol = document.location.protocol,
              httpsDomain = '1d5ef9e9369608f625a8-878b10192d4a956595449977ade9187d.ssl.cf2.rackcdn.com',
              httpDomain = 'trk.cloudamp.net',
              filename = 'ctk.js',
              srcDomain = protocol === 'http:' ? httpDomain : httpsDomain;

          ca_script.type = 'text/javascript';
          ca_script.async = true;
          ca_script.src = protocol + '//' + srcDomain + '/' + filename;
          head[0].appendChild(ca_script);
        })();
        `}
            </Script>
        </div>
    );
}

export default Layout;
